import { defineStore } from 'pinia'

import type { OpsUserRole } from '../pages/ops-users/types'

type LoginUserProfile = {
    firstName?: string
    lastName?: string
    nickname?: string
    profileUrl?: string
    email?: string
    userRole?: OpsUserRole
}

type PersistedUserProfile = {
    userName: string
    email: string
    pfp: string
    userRole: OpsUserRole | null
}

const userProfileStorageKey = 'frontend-admin-user-profile'

const getDefaultUserProfile = (): PersistedUserProfile => ({
    userName: 'Vasili Savitski',
    email: 'vasili@gmail.com',
    pfp: 'https://picsum.photos/id/22/200/300',
    userRole: null,
})

const readStoredUserProfile = (): Partial<PersistedUserProfile> => {
    if (typeof window === 'undefined') {
        return {}
    }

    try {
        const rawProfile = window.localStorage.getItem(userProfileStorageKey)
        if (!rawProfile) {
            return {}
        }

        return JSON.parse(rawProfile) as Partial<PersistedUserProfile>
    } catch {
        return {}
    }
}

const writeStoredUserProfile = (profile: PersistedUserProfile) => {
    if (typeof window === 'undefined') {
        return
    }

    window.localStorage.setItem(userProfileStorageKey, JSON.stringify(profile))
}

export const useUserStore = defineStore('user', {
    state: () => {
        const defaultProfile = getDefaultUserProfile()
        const storedProfile = readStoredUserProfile()

        return {
            userName: storedProfile.userName || defaultProfile.userName,
            email: storedProfile.email || defaultProfile.email,
            memberSince: '8/12/2020',
            pfp: storedProfile.pfp || defaultProfile.pfp,
            userRole: storedProfile.userRole ?? defaultProfile.userRole,
            is2FAEnabled: false,
        }
    },

    actions: {
        toggle2FA() {
            this.is2FAEnabled = !this.is2FAEnabled
        },

        changeUserName(userName: string) {
            this.userName = userName
            writeStoredUserProfile({
                userName: this.userName,
                email: this.email,
                pfp: this.pfp,
                userRole: this.userRole,
            })
        },

        setUserProfile(profile: LoginUserProfile) {
            const nextUserName =
                profile.nickname || [profile.lastName, profile.firstName].filter(Boolean).join(' ').trim()

            this.userName = nextUserName || profile.email || this.userName
            this.email = profile.email || this.email
            this.pfp = profile.profileUrl || this.pfp
            this.userRole = profile.userRole ?? null

            writeStoredUserProfile({
                userName: this.userName,
                email: this.email,
                pfp: this.pfp,
                userRole: this.userRole,
            })
        },
    },
})
